import { Injectable, computed, signal } from '@angular/core';

/**
 * Tracks how many operations are currently in flight so the UI can show a
 * global spinner or disable submit buttons. It deliberately knows nothing
 * about *what* is loading — HTTP calls go through the loading interceptor,
 * and anything else (a slow local computation, a file read) can call
 * `start()` / `stop()` directly.
 */
@Injectable({ providedIn: 'root' })
export class LoadingService {
  private readonly pending = signal(0);

  /** True while at least one operation is in flight. */
  readonly isLoading = computed(() => this.pending() > 0);

  /** Number of operations currently in flight. */
  readonly pendingCount = this.pending.asReadonly();

  start(): void {
    this.pending.update((count) => count + 1);
  }

  stop(): void {
    // Guard against a stray stop() driving the counter negative, which would
    // leave isLoading() stuck on false for the next real request.
    this.pending.update((count) => (count > 0 ? count - 1 : 0));
  }

  /** Clears the counter outright. Useful on hard navigation or error recovery. */
  reset(): void {
    this.pending.set(0);
  }

  /** Runs a promise while keeping the counter accurate even if it rejects. */
  async track<T>(work: Promise<T>): Promise<T> {
    this.start();
    try {
      return await work;
    } finally {
      this.stop();
    }
  }
}
